import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Commercial Access Control Alberta | UniFi Access Installation | Epicom Systems",
  metaDescription:
    "Commercial door access and electronic entry systems across Southern Alberta — UniFi Access, electric strikes and mag locks, intercoms, user management and access control integrated with the rest of the network.",
  canonicalPath: "/access-control",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/access-control#service",
    "name": "Commercial Access Control Installation",
    "serviceType": "Electronic door access and entry control installation",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Electronic access control for commercial doors, gates and multi-user sites across Southern Alberta — UniFi Access, intercoms, electric strikes and mag locks, integrated with UniFi networking and Protect where appropriate.",
    "url": "https://epicom.ca/access-control",
  },
};

export default function AccessControl() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Access Control"
      h1="Access Control for Commercial Doors, Gates & Sites"
      intro="Managing keys becomes a full-time job the moment a business has more than a handful of doors and more than a handful of staff. Electronic access control replaces the key ring with credentials you can issue, restrict and revoke in seconds. Epicom installs commercial access control across Southern Alberta on Ubiquiti UniFi Access, on the same network as the rest of the site — cameras, switching and Wi-Fi included."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/security-cameras", label: "Security Cameras" },
        { to: "/unifi-installation", label: "UniFi Installation" },
        { to: "/commercial-networking", label: "Commercial Networking" },
      ]}
    >
      <H2>What we install</H2>
      <UL>
        <><strong className="text-white/90">UniFi Access door controllers</strong> — networked controllers wired to the door hardware, managed centrally from the same UniFi environment as the rest of the network.</>
        <><strong className="text-white/90">Readers and credentials</strong> — card, fob, mobile-phone-based unlock and PIN-code readers where appropriate. Users are added, removed and grouped in one place.</>
        <><strong className="text-white/90">Electric strikes and maglocks</strong> — the actual door hardware, spec’d and installed for the door in question. We handle the low-voltage side; door hardware carpentry is coordinated with your millwork/GC where needed.</>
        <><strong className="text-white/90">Intercoms and video entry</strong> — audio and video intercoms at entrances where the person unlocking the door is remote from it.</>
        <><strong className="text-white/90">Gate integration</strong> — driveway and yard gates tied into the same access system for sites that have controlled outdoor entry.</>
      </UL>

      <H2>Why electronic access beats keys past a certain size</H2>
      <UL>
        <><strong className="text-white/90">Instant revocation</strong> — a former employee’s access disappears the moment you remove them from the system. No changing locks.</>
        <><strong className="text-white/90">Time-of-day restrictions</strong> — cleaning staff, contractors and after-hours workers can be given access only during their working windows.</>
        <><strong className="text-white/90">Per-door permissions</strong> — the mechanic can get into the shop but not the office; the office manager can get into the office and the mechanical room but not the yard gate.</>
        <><strong className="text-white/90">Audit trail</strong> — every unlock is logged, so when something needs to be reviewed the answer is a report, not a guess.</>
        <><strong className="text-white/90">Shared control</strong> — a manager can grant a temporary code or badge without waiting for a locksmith or a hardware store run.</>
      </UL>

      <H2>Common project types</H2>
      <UL>
        <>Small and medium businesses moving from physical keys to electronic access on office and back-of-house doors.</>
        <>Industrial and shop sites controlling access to the main entry, the shop, and mechanical/electrical rooms.</>
        <>Multi-tenant commercial buildings coordinating shared entrances and per-tenant access.</>
        <>Farms and acreage operations with a mix of yard gates, main shop doors and equipment storage.</>
        <>Sites that already have{" "}
          <a href="/security-cameras" className="text-signal hover:underline">UniFi Protect cameras</a>{" "}
          and want the doors managed in the same environment.</>
      </UL>

      <H2>How it hangs together as a system</H2>
      <H3>One network, one interface</H3>
      <P>
        UniFi Access lives inside the same UniFi environment as the switching, Wi-Fi and
        cameras. That means one place to add or remove a user, one place to review activity,
        and one place to keep the firmware current. For the person actually running the
        business, that consolidation is the difference between a system that gets used and a
        system that gets ignored.
      </P>
      <H3>Wiring done properly</H3>
      <P>
        Each controlled door needs low-voltage cabling from the door hardware back to a
        controller: reader, strike/lock, request-to-exit, and (usually) a position sensor.
        We plan the runs during install and terminate them cleanly, so the finished doors
        latch, unlock and log the way the paperwork says they should.
      </P>
      <H3>Failure modes handled deliberately</H3>
      <P>
        Access systems have to fail in the right direction for the door in question — some
        doors must fail secure (stay locked), some must fail safe (release for egress). We
        pick strikes/maglocks and wire them accordingly, and confirm the behaviour before
        we walk away.
      </P>

      <H2>Working with Epicom on an access control project</H2>
      <P>
        Every site is a bit different, so an access project starts with a walk-through:
        which doors, who uses them, what the current key situation looks like, and where
        the trouble spots are. From there we scope hardware, cabling and controller
        placement, and quote it as one clean bill of materials. The install is coordinated
        with your GC or building people if the doors themselves need any modifications, and
        the finished environment is turned over with the users, groups and permissions
        already set up.
      </P>
    </ServicePageLayout>
  );
}
